import { DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Meta } from "@angular/platform-browser";

import { ExternalPageService } from "../api/services/external-page.service";
import { ExternalPage } from "../api/models/external-page";
import { DomainService } from "../shared/services/domain.service";
import { LocaleService } from "../shared/services/locale.service";
import { isGUID } from "../shared/utils/guid.utils";

export const externalpageImageTypes = {
  MainImage: "MainImage",
  GalleryImage: "GalleryImage",
  LogoImage: "LogoImage",
  ContentImage: "ContentImage",
};

export class ExternalpageSingle {
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly externalPageService = inject(ExternalPageService);
  protected readonly meta = inject(Meta);
  protected readonly domain = inject(DomainService);
  protected readonly locale = inject(LocaleService);

  readonly maxRelatedDataItems = 6;
  externalpageImageTypes = externalpageImageTypes;

  public permalink = "";
  /** Signal-based loading state — works with OnPush change detection */
  public isLoading = signal(false);
  // Using any for backwards compatibility with child classes that override with ModelExternalPage
  public entity: ExternalPage | any = null;

  /**
   * Load single entity data by ID or permalink
   */
  loadSingle(idOrPermalink: string): void {
    this.isLoading.set(true);

    const request$ = isGUID(idOrPermalink)
      ? this.externalPageService.getExternalPage({ id: idOrPermalink })
      : this.externalPageService.getExternalPagesByPermalink({
          permalink: idOrPermalink,
        });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        this.entity = response as ExternalPage;
        this.parseImages();
        this.loadCallback();
        this.setMetaTags();
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error("Failed to load external page", error);
      },
    });
  }

  /**
   * Get the main image URI from imageReferences
   */
  getMainImageUri(): string | null {
    if (!this.entity?.imageReferences?.length) return null;

    for (const image of this.entity.imageReferences) {
      if (image.imageType === externalpageImageTypes.MainImage) {
        return image.imageUri ?? null;
      }
    }
    return null;
  }

  protected parseImages(): void {
    if (!this.entity) return;
    this.entity.imageUri = this.getMainImageUri() ?? '';
  }

  protected loadCallback(): void {
    // Override in child classes
  }

  protected onIdChange(): void {
    this.scrollToTop();
  }

  protected scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  /**
   * Get translated title based on active language
   * @param entityData Optional entity to get title from, defaults to this.entity
   */
  getTranslatedTitle(entityData?: ExternalPage | any): string {
    const target = entityData ?? this.entity;
    if (!target) return "";

    const language = this.locale.getCurrentLanguage().toLowerCase();

    switch (language) {
      case "en":
        return target.titleEn ?? target.title;
      case "de":
        return target.titleDe ?? target.title;
      case "fr":
        return target.titleFr ?? target.title;
      case "pt":
        return target.titlePt ?? target.title;
      default:
        return target.title;
    }
  }

  /**
   * Get translated content based on active language
   * @param entityData Optional entity to get content from, defaults to this.entity
   */
  getTranslatedContent(entityData?: ExternalPage | any): string {
    const target = entityData ?? this.entity;
    if (!target) return "";

    const language = this.locale.getCurrentLanguage().toLowerCase();

    switch (language) {
      case "en":
        return target.contentEn ?? target.content ?? "";
      case "de":
        return target.contentDe ?? target.content ?? "";
      case "fr":
        return target.contentFr ?? target.content ?? "";
      case "pt":
        return target.contentPt ?? target.content ?? "";
      default:
        return target.content ?? "";
    }
  }

  protected setMetaTags(): void {
    if (!this.entity) return;

    let suffix = "";
    let fieldMetaDescription: keyof ExternalPage = "metaDescription";
    let fieldMetaKeywords: keyof ExternalPage = "metaKeywords";

    const region = this.domain.getCurrentRegion();
    const language = this.locale.getCurrentLanguage();

    if (region === "BR" || language === "pt") {
      suffix = "Pt";
    } else if (region === "DE" || language === "de") {
      suffix = "De";
    } else if (region === "EU" || language === "en") {
      suffix = "En";
    }

    if (suffix) {
      fieldMetaDescription = `metaDescription${suffix}` as keyof ExternalPage;
      fieldMetaKeywords = `metaKeywords${suffix}` as keyof ExternalPage;
    }

    const metaDescription =
      (this.entity[fieldMetaDescription] as string | null | undefined) ??
      this.entity.metaDescription ??
      "";
    const metaKeywords =
      (this.entity[fieldMetaKeywords] as string | null | undefined) ??
      this.entity.metaKeywords ??
      "";

    this.meta.updateTag({
      name: "description",
      content: metaDescription,
    });
    this.meta.updateTag({
      name: "keywords",
      content: metaKeywords,
    });

    const imageUri = this.getMainImageUri();
    this.meta.updateTag({
      name: "og:image",
      content: imageUri ?? "",
    });
  }
}
